// Where a sign-up first came from (#1454). The contract, including the cookie
// format, is docs/signup-attribution.md. Pure (the caller reads the cookie and
// the URL) so it can be unit-tested in the Node Jasmine harness.

/** The `attribution` member of `POST api/accounts/create`. The server cleans every value. */
export interface SignupAttribution {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    content: string | null;
    term: string | null;
    referrerHost: string | null;
    landingPath: string | null;
    firstTouchUtc: string | null;
}

/**
 * A parsed query in `$location.search()`'s shape: a value is a string, `true`
 * for a key with no `=`, or an array for a repeated key.
 */
export type QueryValues = Readonly<Record<string, unknown>>;

export interface SignupAttributionSources {
    /** `document.cookie`. */
    cookies: string;
    /** The sign-up URL's queries, in the order to try them. */
    queries: ReadonlyArray<QueryValues>;
    /** The time of submit. */
    now: Date;
}

export const FIRST_TOUCH_COOKIE = 'plan_first_touch';

const SIGNUP_LANDING_PATH = '/signup';

/**
 * The first source that carries a channel, without merging across them: the
 * first-touch cookie, then an `ft` parameter, then plain `utm_*` parameters.
 * Undefined when none does.
 */
export function signupAttribution(sources: SignupAttributionSources): SignupAttribution | undefined {
    const fromCookie = firstTouchFromCookies(sources.cookies);
    if (fromCookie) {
        return fromCookie;
    }

    for (const query of sources.queries) {
        const ft = firstString(query['ft']);
        // The query parser has already undone the encodeURIComponent the cookie
        // still carries. Decoding again would corrupt a value holding an
        // encoded '&' or '%'.
        const fromFt = ft === null ? undefined : firstTouchFromQuery(ft);
        if (fromFt) {
            return fromFt;
        }
    }

    for (const query of sources.queries) {
        const fromUtm = touchFromUtmParameters(query, sources.now);
        if (fromUtm) {
            return fromUtm;
        }
    }

    return undefined;
}

/** A query string such as `window.location.search`, first value per key. */
export function queryValues(search: string): QueryValues {
    const values: Record<string, string> = {};
    new URLSearchParams(search).forEach((value, key) => {
        if (!Object.prototype.hasOwnProperty.call(values, key)) {
            values[key] = value;
        }
    });
    return values;
}

/** The first-touch cookie in a `document.cookie` string, if one parses to a touch. */
export function firstTouchFromCookies(cookies: string): SignupAttribution | undefined {
    for (const pair of cookies.split(';')) {
        const split = pair.indexOf('=');
        if (split < 0 || pair.slice(0, split).trim() !== FIRST_TOUCH_COOKIE) {
            continue;
        }

        let decoded: string;
        try {
            decoded = decodeURIComponent(pair.slice(split + 1).trim());
        } catch {
            // A malformed escape: this value is unusable, but another cookie of
            // the same name, set for a different domain or path, may not be.
            continue;
        }

        const touch = firstTouchFromQuery(decoded);
        if (touch) {
            return touch;
        }
    }
    return undefined;
}

/** A decoded first-touch value: a query string with the contract's short keys. */
function firstTouchFromQuery(query: string): SignupAttribution | undefined {
    const params = new URLSearchParams(query);

    // A later format may give the keys another meaning.
    const version = params.get('v');
    if (version !== null && version !== '1') {
        return undefined;
    }

    return withChannel({
        source: text(params.get('src')),
        medium: text(params.get('med')),
        campaign: text(params.get('cmp')),
        content: text(params.get('cnt')),
        term: text(params.get('trm')),
        referrerHost: text(params.get('ref')),
        landingPath: text(params.get('lp')),
        firstTouchUtc: text(params.get('ts')),
    });
}

/** An email or QR code linking straight to the app: it lands on the sign-up page, now. */
function touchFromUtmParameters(query: QueryValues, now: Date): SignupAttribution | undefined {
    return withChannel({
        source: text(firstString(query['utm_source'])),
        medium: text(firstString(query['utm_medium'])),
        campaign: text(firstString(query['utm_campaign'])),
        content: text(firstString(query['utm_content'])),
        term: text(firstString(query['utm_term'])),
        referrerHost: null,
        landingPath: SIGNUP_LANDING_PATH,
        firstTouchUtc: now.toISOString(),
    });
}

/**
 * The server discards a touch with no channel field, so one must not win over
 * a later source that has one.
 */
function withChannel(touch: SignupAttribution): SignupAttribution | undefined {
    const hasChannel = touch.source !== null || touch.medium !== null || touch.campaign !== null
        || touch.content !== null || touch.term !== null || touch.referrerHost !== null;
    return hasChannel ? touch : undefined;
}

function firstString(value: unknown): string | null {
    if (typeof value === 'string') {
        return value;
    }
    if (Array.isArray(value)) {
        const first = value.find(item => typeof item === 'string');
        return first === undefined ? null : first;
    }
    return null;
}

function text(value: string | null): string | null {
    const trimmed = value === null ? '' : value.trim();
    return trimmed.length === 0 ? null : trimmed;
}
